import { Text, Icon } from '@pairy/pds-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMemo } from 'react';

import useFileVersionList from '@/hooks/queries/file/useFileVersionList';
import { getUTCDate } from '@/utils/getUTCDate';

import * as Styled from './index.styles';

const FILE_NAME_SIZE = 20;
const SELECTED_FILE_NAME_SIZE = 10;

interface Props {
  originalFileId: number;
  selectedFileVersionId: number | null;
  clientId?: number;
  handleFileVersionChange: (id: number) => void;
}

const getSliceName = (fileName: string, size: number) => {
  if (fileName.length > size) {
    return fileName.substring(0, size) + '...';
  }
  return fileName;
};

const VersionDropdown = ({
  clientId,
  originalFileId,
  selectedFileVersionId,
  handleFileVersionChange,
}: Props) => {
  const { data: file } = useFileVersionList(
    { fileId: originalFileId, clientId },
    {
      select: (data) => data.data.data.file,
      enabled: Boolean(originalFileId),
    },
  );

  const selectedFile = useMemo(
    () => file?.find((item) => item.id === selectedFileVersionId),
    [file, originalFileId, selectedFileVersionId],
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Styled.TriggerButton gap={4} padding={4} alignItems="center">
          <Text variant="lg-semibold" color="zinc_500">
            {getSliceName(selectedFile?.name ?? '', SELECTED_FILE_NAME_SIZE)}
          </Text>
          {selectedFileVersionId === originalFileId ? (
            <Text variant="sm-medium" color="skyblue_400">
              현재 버전
            </Text>
          ) : (
            selectedFile?.editedAt && (
              <Text variant="sm-medium" color="zinc_400">
                {getUTCDate(selectedFile.editedAt, 'yearMonthDay')} 이전 버전
              </Text>
            )
          )}

          <Icon icon="ph:caret-down" size={17} />
        </Styled.TriggerButton>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <Styled.VersionDropdownContent>
          {file?.map(({ id, name, editedAt }) => {
            if (id === selectedFileVersionId) {
              return (
                <Styled.SelectedDropDownItem
                  key={id}
                  onClick={() => handleFileVersionChange(id)}
                >
                  <Text variant="md-medium" color="zinc_500">
                    {getSliceName(name, FILE_NAME_SIZE)}
                  </Text>
                  <Text variant="sm-medium" color="inherit">
                    선택됨
                  </Text>
                </Styled.SelectedDropDownItem>
              );
            } else if (id === originalFileId) {
              return (
                <Styled.DropDownItem
                  key={id}
                  onClick={() => handleFileVersionChange(id)}
                >
                  <Text variant="md-medium" color="zinc_500">
                    {getSliceName(name, FILE_NAME_SIZE)}
                  </Text>
                  <Text variant="sm-medium" color="inherit">
                    최신 버전
                  </Text>
                </Styled.DropDownItem>
              );
            } else {
              return (
                <Styled.DropDownItem
                  key={id}
                  onClick={() => handleFileVersionChange(id)}
                >
                  <Styled.FileNameText variant="md-medium" as="div">
                    {getSliceName(name, FILE_NAME_SIZE)}
                  </Styled.FileNameText>
                  {editedAt && (
                    <Text variant="sm-medium" color="inherit">
                      {getUTCDate(editedAt, 'yearMonthDay')} 이전 버전
                    </Text>
                  )}
                </Styled.DropDownItem>
              );
            }
          })}
        </Styled.VersionDropdownContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default VersionDropdown;
